import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

export enum TenantFileUploadType {
  FAVICON = 100,
  LOGO_LIGHT_LARGE = 101,
  LOGO_LIGHT_MEDIUM = 102,
  LOGO_DARK_LARGE = 103,
  LOGO_DARK_MEDIUM = 104,
}

export interface TenantImage {
  _id: string;
  filename: string;
  mimeType: string;
  dataUrl?: string;
  metadata: { type: TenantFileUploadType };
}

@Injectable({ providedIn: 'root' })
export class TenantSettingsService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl + '/file-server';
  public publicBaseUrl = environment.apiUrl + '/file-server/public';

  private tenantImages$ = new BehaviorSubject<TenantImage[]>([]);

  getTenantImages(): Observable<TenantImage[]> {
    return this.tenantImages$.asObservable();
  }

  loadTenantImages(): Observable<TenantImage[]> {
    return this.http.get<TenantImage[]>(`${this.baseUrl}/getTenantImages`).pipe(
      tap((images) => this.tenantImages$.next(images)),
    );
  }

  uploadTenantImage(file: File, type: TenantFileUploadType): Observable<TenantImage[]> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', String(type));
    return this.http.post<TenantImage[]>(`${this.baseUrl}/uploadTenantImage`, formData).pipe(
      tap(() => this.loadTenantImages().subscribe()),
    );
  }

  getImageUrl(type: TenantFileUploadType): string | null {
    const image = this.tenantImages$.value.find((i) => i.metadata?.type === type);
    if (!image) return null;
    return image.dataUrl ?? `${this.publicBaseUrl}/${image.filename}`;
  }

  getFaviconUrl(): string | null {
    return this.getImageUrl(TenantFileUploadType.FAVICON);
  }

  getLogoUrl(theme: 'light' | 'dark', size: 'large' | 'medium'): string | null {
    const type = theme === 'light'
      ? (size === 'large' ? TenantFileUploadType.LOGO_LIGHT_LARGE : TenantFileUploadType.LOGO_LIGHT_MEDIUM)
      : (size === 'large' ? TenantFileUploadType.LOGO_DARK_LARGE : TenantFileUploadType.LOGO_DARK_MEDIUM);
    return this.getImageUrl(type);
  }

  deleteTenantImages(uploadType: TenantFileUploadType): Observable<void[]> {
    const storageTypes: TenantFileUploadType[] = uploadType === TenantFileUploadType.FAVICON
      ? [TenantFileUploadType.FAVICON]
      : uploadType === TenantFileUploadType.LOGO_LIGHT_LARGE
        ? [TenantFileUploadType.LOGO_LIGHT_LARGE, TenantFileUploadType.LOGO_LIGHT_MEDIUM]
        : [TenantFileUploadType.LOGO_DARK_LARGE, TenantFileUploadType.LOGO_DARK_MEDIUM];

    const ids = this.tenantImages$.value
      .filter((i) => storageTypes.includes(i.metadata?.type))
      .map((i) => i._id);

    const deletes$ = ids.map((id) => this.http.delete<void>(`${this.baseUrl}/${id}`));

    return new Observable((observer) => {
      if (deletes$.length === 0) { observer.next([]); observer.complete(); return; }
      Promise.all(deletes$.map((d) => d.toPromise())).then(() => {
        this.loadTenantImages().subscribe(() => { observer.next([]); observer.complete(); });
      }).catch((err) => observer.error(err));
    });
  }
}
